/**
 * Classic Shop — <cs-hero-carousel>
 * Markup + styles: sections/cs-hero-carousel.liquid.
 *
 * Crossfade carousel, infinite loop. Autoplay pauses while the pointer is over
 * it, while it has keyboard focus, while the tab is hidden or the carousel is
 * off-screen, and stops entirely via the pause button. prefers-reduced-motion
 * starts it paused. Arrows / indicators / ← → keys / touch swipe navigate.
 */
(() => {
  if (customElements.get('cs-hero-carousel')) return;

  const SWIPE_MIN = 40;

  class CsHeroCarousel extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('.cs-hc__track');
      this.slides = Array.from(this.querySelectorAll('.cs-hc__slide'));
      this.dots = Array.from(this.querySelectorAll('.cs-hc__dot'));
      this.toggleButton = this.querySelector('[data-action="toggle"]');
      if (!this.track || this.slides.length === 0) return;

      this.index = Math.max(0, this.slides.findIndex((s) => s.classList.contains('is-active')));
      this.interval = Number(this.dataset.interval) || 6000;
      this.canAutoplay = this.dataset.autoplay === 'true' && this.slides.length > 1;

      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      // Explicit visitor choice (pause button). Reduced motion starts paused.
      this.userPaused = !this.canAutoplay || this.reducedMotion.matches;
      // Temporary holds that freeze the timer without changing the visitor's choice.
      this.holds = new Set();
      this.timer = null;
      this.remaining = this.interval;
      this.startedAt = 0;

      this.abort = new AbortController();
      const opts = { signal: this.abort.signal };

      this.addEventListener('click', this.onClick.bind(this), opts);
      this.addEventListener('keydown', this.onKeydown.bind(this), opts);

      if (this.dataset.pauseOnHover === 'true') {
        this.addEventListener('pointerenter', (e) => e.pointerType === 'mouse' && this.hold('hover'), opts);
        this.addEventListener('pointerleave', (e) => e.pointerType === 'mouse' && this.release('hover'), opts);
      }
      // Keyboard focus only: a mouse click on an arrow must not leave it paused.
      this.addEventListener('focusin', (e) => {
        if (e.target.matches?.(':focus-visible')) this.hold('focus');
      }, opts);
      this.addEventListener('focusout', (e) => {
        if (!this.contains(e.relatedTarget)) this.release('focus');
      }, opts);

      document.addEventListener('visibilitychange', () => {
        document.hidden ? this.hold('hidden') : this.release('hidden');
      }, opts);

      this.reducedMotion.addEventListener('change', (e) => {
        if (e.matches) this.setUserPaused(true);
      }, opts);

      this.bindSwipe(opts);
      this.bindEditor(opts);

      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(([entry]) => {
          entry.isIntersecting ? this.release('offscreen') : this.hold('offscreen');
        }, { threshold: 0.2 });
        this.observer.observe(this);
      }

      this.render();
      this.syncPlayState();
      this.classList.add('is-ready');
    }

    disconnectedCallback() {
      this.abort?.abort();
      this.observer?.disconnect();
      clearTimeout(this.timer);
    }

    /* ── Navigation ── */

    goTo(index) {
      const count = this.slides.length;
      const next = ((index % count) + count) % count;
      if (next === this.index) return;
      this.index = next;
      this.render();
      this.restartTimer();
    }

    next() {
      this.goTo(this.index + 1);
    }

    prev() {
      this.goTo(this.index - 1);
    }

    render() {
      this.slides.forEach((slide, i) => {
        const active = i === this.index;
        slide.classList.toggle('is-active', active);
        slide.toggleAttribute('inert', !active);
      });
      this.dots.forEach((dot, i) => {
        if (i === this.index) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });
      this.restartProgress();
    }

    /* ── Autoplay ── */

    isPlaying() {
      return this.canAutoplay && !this.userPaused;
    }

    hold(reason) {
      this.holds.add(reason);
      this.syncPlayState();
    }

    release(reason) {
      if (!this.holds.delete(reason)) return;
      this.syncPlayState();
    }

    setUserPaused(paused) {
      this.userPaused = paused;
      // An explicit "play" overrides interaction holds (WAI-ARIA carousel pattern).
      if (!paused) {
        this.holds.delete('focus');
        this.holds.delete('hover');
      }
      this.remaining = this.interval;
      this.restartProgress();
      this.syncPlayState();
    }

    syncPlayState() {
      const playing = this.isPlaying();
      const held = this.holds.size > 0;

      this.classList.toggle('is-playing', playing);
      this.classList.toggle('is-paused', !playing);
      this.classList.toggle('is-held', held);
      // Silent while rotating on its own; slide changes are announced otherwise.
      this.track.setAttribute('aria-live', playing && !held ? 'off' : 'polite');

      if (this.toggleButton) {
        const label = playing ? this.toggleButton.dataset.labelPause : this.toggleButton.dataset.labelPlay;
        this.toggleButton.setAttribute('aria-label', label);
      }

      clearTimeout(this.timer);
      this.timer = null;

      if (!playing) return;

      if (held) {
        // Freeze: remember how much of the current interval is left.
        if (this.startedAt) {
          this.remaining = Math.max(0, this.remaining - (performance.now() - this.startedAt));
          this.startedAt = 0;
        }
        return;
      }

      this.startedAt = performance.now();
      this.timer = setTimeout(() => {
        this.startedAt = 0;
        this.remaining = this.interval;
        this.next();
      }, this.remaining);
    }

    restartTimer() {
      this.remaining = this.interval;
      this.startedAt = 0;
      this.syncPlayState();
    }

    restartProgress() {
      // Restart the CSS progress animation on the active indicator.
      const fill = this.dots[this.index]?.querySelector('.cs-hc__dot-fill');
      if (!fill) return;
      fill.style.animation = 'none';
      void fill.offsetWidth;
      fill.style.animation = '';
    }

    /* ── Input ── */

    onClick(event) {
      const button = event.target.closest('[data-action]');
      if (!button || !this.contains(button)) return;
      const { action } = button.dataset;
      if (action === 'prev') this.prev();
      else if (action === 'next') this.next();
      else if (action === 'go') this.goTo(Number(button.dataset.index));
      else if (action === 'toggle') this.setUserPaused(this.isPlaying());
    }

    onKeydown(event) {
      if (event.defaultPrevented || event.altKey || event.ctrlKey || event.metaKey) return;
      if (event.target.closest('input, textarea, select, [contenteditable]')) return;
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this.prev();
        this.focusActiveDot(event);
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        this.next();
        this.focusActiveDot(event);
      }
    }

    focusActiveDot(event) {
      // Keep roving focus on the indicators when navigating from them.
      if (event.target.classList?.contains('cs-hc__dot')) this.dots[this.index]?.focus();
    }

    bindSwipe(opts) {
      let startX = 0;
      let startY = 0;
      let tracking = false;
      let swiped = false;

      this.track.addEventListener('pointerdown', (e) => {
        // Any new press starts fresh — a mouse/pen click after a touch swipe must not be swallowed.
        swiped = false;
        if (e.pointerType === 'mouse' || !e.isPrimary) return;
        tracking = true;
        startX = e.clientX;
        startY = e.clientY;
      }, opts);

      const end = (e) => {
        if (!tracking) return;
        tracking = false;
        const dx = e.clientX - startX;
        const dy = e.clientY - startY;
        if (Math.abs(dx) < SWIPE_MIN || Math.abs(dx) < Math.abs(dy) * 1.2) return;
        swiped = true;
        // Only the click synthesized by this same gesture is suppressed.
        setTimeout(() => (swiped = false), 400);
        dx < 0 ? this.next() : this.prev();
      };

      this.track.addEventListener('pointerup', end, opts);
      this.track.addEventListener('pointercancel', () => (tracking = false), opts);

      // A swipe that ends on the CTA must not also follow the link.
      this.track.addEventListener('click', (e) => {
        if (!swiped) return;
        swiped = false;
        e.preventDefault();
        e.stopPropagation();
      }, { ...opts, capture: true });
    }

    bindEditor(opts) {
      if (!window.Shopify?.designMode) return;
      const own = (e) => this.slides.findIndex((s) => s.dataset.blockId === e.detail?.blockId);

      document.addEventListener('shopify:block:select', (e) => {
        const i = own(e);
        if (i < 0) return;
        this.hold('editor');
        this.goTo(i);
      }, opts);

      document.addEventListener('shopify:block:deselect', (e) => {
        if (own(e) >= 0) this.release('editor');
      }, opts);
    }
  }

  customElements.define('cs-hero-carousel', CsHeroCarousel);
})();
